// src/components/common/GlobalSearch.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '../../hooks/useProjects';
import { useExpenses } from '../../hooks/useExpenses';
import {
    MagnifyingGlassIcon, XMarkIcon, FolderOpenIcon,
    BanknotesIcon, ArrowRightIcon,
    CommandLineIcon
} from '@heroicons/react/24/outline';

const CATEGORY_LABELS = {
    MATERIALS: 'Materiali', EQUIPMENT: 'Attrezzature', LABOR: 'Manodopera',
    FUEL: 'Carburante', SUBCONTRACT: 'Subappalto', TRANSPORT: 'Trasporto',
    PERMITS: 'Permessi', ADMIN: 'Amministrazione', OTHER: 'Altro',
};

const highlight = (text, query) => {
    if (!query || !text) return text || '';
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
        regex.test(part)
            ? <mark key={i} className="bg-yellow-200 text-yellow-900 rounded px-0.5">{part}</mark>
            : part
    );
};

const GlobalSearch = () => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef(null);
    const containerRef = useRef(null);

    const { projects } = useProjects({ limit: 100 });
    const { expenses } = useExpenses({ limit: 1000 });

    // Keyboard shortcut: Ctrl+K / Cmd+K
    useEffect(() => {
        const handler = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                setOpen(true);
            }
            if (e.key === 'Escape') setOpen(false);
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    // Focus input when opened
    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 50);
            setQuery('');
            setActiveIndex(0);
        }
    }, [open]);

    // Click outside to close
    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // Search results
    const results = useCallback(() => {
        if (!query.trim() || query.length < 2) return [];
        const q = query.toLowerCase();
        const items = [];

        // Progetti
        projects.filter(p =>
            p.name?.toLowerCase().includes(q) ||
            p.code?.toLowerCase().includes(q) ||
            p.location?.toLowerCase().includes(q)
        ).slice(0, 4).forEach(p => items.push({
            type: 'project',
            id: p.id,
            title: p.name,
            subtitle: `${p.code} · ${p.status} · ${p.progress || 0}% avanzamento`,
            icon: FolderOpenIcon,
            iconBg: 'bg-blue-100',
            iconColor: 'text-blue-600',
            badge: p.status,
            path: `/projects/${p.id}`,
        }));

        // Spese
        expenses.filter(e =>
            e.reference?.toLowerCase().includes(q) ||
            e.description?.toLowerCase().includes(q) ||
            e.supplierName?.toLowerCase().includes(q) ||
            e.invoiceNumber?.toLowerCase().includes(q)
        ).slice(0, 4).forEach(e => items.push({
            type: 'expense',
            id: e.id,
            title: e.reference,
            subtitle: `${e.project?.name || '—'} · ${CATEGORY_LABELS[e.category] || e.category} · ${e.supplierName || ''}`,
            icon: BanknotesIcon,
            iconBg: 'bg-emerald-100',
            iconColor: 'text-emerald-600',
            badge: e.validated ? 'Validata' : 'In attesa',
            badgeColor: e.validated ? 'bg-emerald-100 text-emerald-700' : 'bg-yellow-100 text-yellow-700',
            path: `/expenses/${e.id}/edit`,
        }));

        return items;
    }, [query, projects, expenses]);

    const items = results();

    // Keyboard navigation
    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(i => Math.min(i + 1, items.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter' && items[activeIndex]) {
            navigate(items[activeIndex].path);
            setOpen(false);
        }
    };

    const handleSelect = (path) => {
        navigate(path);
        setOpen(false);
    };

    // Quick actions (sempre visibili)
    const quickActions = [
        { label: 'Nuovo Progetto', path: '/projects/new', icon: FolderOpenIcon, shortcut: null },
        { label: 'Nuova Spesa', path: '/expenses/new', icon: BanknotesIcon, shortcut: null },
        { label: 'Dashboard', path: '/dashboard', icon: CommandLineIcon, shortcut: null },
    ];

    if (!open) {
        return (
            <button
                onClick={() => setOpen(true)}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 rounded-xl transition text-sm"
            >
                <MagnifyingGlassIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Cerca...</span>
                <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-white border border-gray-200 rounded text-xs text-gray-400 font-mono">
                    ⌘K
                </kbd>
            </button>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4">
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />

            {/* Search box */}
            <div ref={containerRef} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">

                {/* Input */}
                <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-100">
                    <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={e => { setQuery(e.target.value); setActiveIndex(0); }}
                        onKeyDown={handleKeyDown}
                        placeholder="Cerca progetti, spese, fornitori..."
                        className="flex-1 text-sm text-gray-900 placeholder-gray-400 outline-none"
                    />
                    {query && (
                        <button onClick={() => setQuery('')} className="p-1 text-gray-400 hover:text-gray-600">
                            <XMarkIcon className="w-4 h-4" />
                        </button>
                    )}
                    <kbd className="hidden sm:inline-flex px-2 py-1 bg-gray-100 border border-gray-200 rounded text-xs text-gray-400 font-mono">
                        ESC
                    </kbd>
                </div>

                {/* Risultati */}
                <div className="max-h-96 overflow-y-auto">
                    {query.length >= 2 ? (
                        items.length === 0 ? (
                            <div className="py-12 text-center">
                                <MagnifyingGlassIcon className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                                <p className="text-sm font-medium text-gray-500">Nessun risultato per "{query}"</p>
                                <p className="text-xs text-gray-400 mt-1">Prova con un termine diverso</p>
                            </div>
                        ) : (
                            <div className="p-2">
                                {/* Raggruppa per tipo */}
                                {['project', 'expense'].map(type => {
                                    const typeItems = items.filter(i => i.type === type);
                                    if (typeItems.length === 0) return null;
                                    const typeLabels = { project: '🏗️ Progetti', expense: '💰 Spese' };
                                    return (
                                        <div key={type} className="mb-2">
                                            <p className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                                {typeLabels[type]}
                                            </p>
                                            {typeItems.map((item, idx) => {
                                                const globalIdx = items.indexOf(item);
                                                const Icon = item.icon;
                                                return (
                                                    <button
                                                        key={item.id}
                                                        onClick={() => handleSelect(item.path)}
                                                        onMouseEnter={() => setActiveIndex(globalIdx)}
                                                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition ${activeIndex === globalIdx ? 'bg-blue-50' : 'hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        <div className={`w-9 h-9 ${item.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                                                            <Icon className={`w-5 h-5 ${item.iconColor}`} />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                                {highlight(item.title, query)}
                                                            </p>
                                                            <p className="text-xs text-gray-400 truncate mt-0.5">
                                                                {highlight(item.subtitle, query)}
                                                            </p>
                                                        </div>
                                                        {item.badge && (
                                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${item.badgeColor || 'bg-gray-100 text-gray-600'
                                                                }`}>
                                                                {item.badge}
                                                            </span>
                                                        )}
                                                        <ArrowRightIcon className={`w-3.5 h-3.5 flex-shrink-0 ${activeIndex === globalIdx ? 'text-blue-500' : 'text-gray-300'}`} />
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    ) : (
                        // Quick actions
                        <div className="p-2">
                            <p className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                Azioni rapide
                            </p>
                            {quickActions.map((action, i) => {
                                const Icon = action.icon;
                                return (
                                    <button
                                        key={action.path}
                                        onClick={() => handleSelect(action.path)}
                                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 text-left transition"
                                    >
                                        <div className="w-9 h-9 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                            <Icon className="w-5 h-5 text-gray-500" />
                                        </div>
                                        <span className="text-sm font-medium text-gray-700">{action.label}</span>
                                        <ArrowRightIcon className="w-3.5 h-3.5 text-gray-300 ml-auto" />
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50 flex items-center gap-4 text-xs text-gray-400">
                    <span className="flex items-center gap-1"><kbd className="bg-white border border-gray-200 px-1.5 py-0.5 rounded font-mono">↑↓</kbd> naviga</span>
                    <span className="flex items-center gap-1"><kbd className="bg-white border border-gray-200 px-1.5 py-0.5 rounded font-mono">↵</kbd> apri</span>
                    <span className="flex items-center gap-1"><kbd className="bg-white border border-gray-200 px-1.5 py-0.5 rounded font-mono">ESC</kbd> chiudi</span>
                    {items.length > 0 && <span className="ml-auto">{items.length} risultati</span>}
                </div>
            </div>
        </div>
    );
};

export default GlobalSearch;
